import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import L from 'leaflet';
import { FeatureGroup, useMap } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import { leafletDrawEditControlEventsFunctions } from './functions/events/base/leafletDrawEditControlEventsFunctions';
import { drawOptions } from './settings/drawOptions';
import SinglePolygon from '../MapShapeLayers/PolygonLayer/SinglePolygon/SinglePolygon';
import {
    multiPolygonEditedIDState,
    isAuthorizedToEditState,
    isDrawPolygonActiveState,
    isEditableState,
    isMeasuringToolDeleteClickedState,
    isPolygonDragActiveState,
    isUsingMeasuringToolState,
    singlePolygonCoordsState,
    mapLayersState,
    mapOptionsState,
    distanceBetweenGeneratedPointsState,
    isSlopeServiceActiveState,
    isMapZoomingState,
    deletedDrawnPolygonIdState,
    isEditDrawnPolygonsState,
    editedDrawnPolygonInfoState,
    isEarthEngineServiceActiveState,
} from '../../../../app/redux/retrievers/retrievers';
import { disableEditMode, enableEditMode, removeClearAllButton } from '../../base/functions/base/LeafletBaseFunctions';
import { editOptions } from './settings/editOptions';
import { enableDragging } from './controllers/enableDragging';
import { disableDragging } from './controllers/disableDragging';
import { measuringToolOptions } from './settings/measuringToolOptions';
import { measuringToolController } from './controllers/measuringToolController';
import EditControlCustomButtons from './components/EditControlCustomButtons/EditControlCustomButtons';
import { setIsMeasuringToolDeleteClicked } from '../../../../app/redux/slices/isMeasuringToolDeleteClickedSlice';
import SingleMultiPolygonEditable from '../MapShapeLayers/PolygonLayer/MultiPolygons/SingleMultiPolygonEditable/SingleMultiPolygonEditable';
import { removeDeleteButton, removeEditTools, restoreDeleteButton } from '../../base/functions/removeMapButtons';

interface EditLayerProps {
    // setMapLayersData: React.Dispatch<React.SetStateAction<never[]>>;
    mapRef: any;
    isMapLocked: boolean;
}

const EditLayer: React.FC<EditLayerProps> = ({ mapRef, isMapLocked }) => {
    const map = useMap();
    const featureGroupRef = useRef(null);
    const dispatch = useDispatch();
    //*redux states
    const mapOptions = useSelector(mapOptionsState);
    const mapLayerIDs = useSelector(mapLayersState);
    const singlePolygonCoords = useSelector(singlePolygonCoordsState);
    const isAuthorizedToEdit = useSelector(isAuthorizedToEditState);
    const isEditable = useSelector(isEditableState);
    const isPolygonDragActive = useSelector(isPolygonDragActiveState);
    const isUsingMeasuringTool = useSelector(isUsingMeasuringToolState);
    const isMeasuringToolDeleteClicked = useSelector(isMeasuringToolDeleteClickedState);
    const isEarthEngineServiceActive = useSelector(isEarthEngineServiceActiveState);
    const multiPolygonEditedId = useSelector(multiPolygonEditedIDState);
    const isDrawPolygonActive = useSelector(isDrawPolygonActiveState);
    const isEditDrawnPolygons = useSelector(isEditDrawnPolygonsState);
    const editedDrawnPolygonData = useSelector(editedDrawnPolygonInfoState);
    const deletedDrawnPolygonId = useSelector(deletedDrawnPolygonIdState);
    const distanceBetweenGeneratedPoints = useSelector(distanceBetweenGeneratedPointsState);
    const isSlopeServiceActive = useSelector(isSlopeServiceActiveState);
    const isMapZooming = useSelector(isMapZoomingState);
    const [editControlKey, setEditControlKey] = useState(0);
    //*react states
    const [isEditControlMounted, setIsEditControlMounted] = useState(false);
    const [measuringToolDrawn, setMeasuringToolDrawn] = useState(false);
    const [measuringToolCheckToggle, setMeasuringToolCheckToggle] = useState(false);
    const [measuringToolLines, setMeasuringToolLines] = useState([]);
    const [measuringToolIds, setMeasuringToolIds] = useState<number[]>([]);

    /*-----------------events for EditControl component-----------------*/
    const {
        onMounted,
        onDrawStart,
        onDrawStop,
        onCreated,
        onEditStart,
        onEditStop,
        onEditMove,
        onEdited,
        onDeleteStart,
        onDeleteStop,
        onDeleted,
    } = leafletDrawEditControlEventsFunctions(
        map,
        mapRef,
        dispatch,
        featureGroupRef,
        mapLayerIDs,
        singlePolygonCoords,
        isUsingMeasuringTool,
        measuringToolDrawn,
        measuringToolCheckToggle,
        multiPolygonEditedId,
        distanceBetweenGeneratedPoints,
        isSlopeServiceActive,
        isEarthEngineServiceActive,
        setIsEditControlMounted,
        setMeasuringToolDrawn,
        setMeasuringToolCheckToggle,
        setMeasuringToolLines,
        setMeasuringToolIds,
    );

    /*------------------------------------------------------------------*/

    if (!mapOptions.tools.editButton && !mapOptions.tools.deleteButton) {
        removeEditTools();
        removeDeleteButton();
    }

    /*-------------------------------polygon dragging control------------------------------*/
    isPolygonDragActive || multiPolygonEditedId !== 0
        ? enableDragging(featureGroupRef, multiPolygonEditedId, measuringToolIds)
        : disableDragging(featureGroupRef);
    /*------------------------------------------------------------------------------------*/

    /*-------initialize polyline for measuring tool-------*/
    const measuringToolPolyline = useMemo(
        () =>
            //@ts-ignore
            new L.Draw.Polyline(map, measuringToolOptions),
        [map, measuringToolOptions],
    );
    /*----------------------------------------------------*/

    /*--------------------edit button activation control--------------------*/
    useEffect(() => {
        /*---removes clear all button option from delete button menu---*/
        // removeClearAllButton();
        if (isAuthorizedToEdit) {
            disableEditMode();
        }
        if (multiPolygonEditedId !== 0 || isEditDrawnPolygons) {
            if (isAuthorizedToEdit) {
                disableEditMode();
            }
            if (
                isEditControlMounted &&
                isAuthorizedToEdit
                // &&
                // (isEditable || multiPolygonEditedId || isDrawPolygonActive)
            ) {
                enableEditMode();
            }
        }
    }, [
        isEditControlMounted,
        isAuthorizedToEdit,
        isEditable,
        mapLayerIDs,
        multiPolygonEditedId,
        isEditDrawnPolygons,
        // drawnPolygonIdBackup,
    ]);
    /*----------------------------------------------------------------------*/

    /*------------------------drawn polygon edit control------------------------*/
    useEffect(() => {
        if (editedDrawnPolygonData.polygonId) {
            //@ts-ignore
            const layerToEdit = map._layers[editedDrawnPolygonData.polygonId];
            layerToEdit && layerToEdit.setStyle({ color: editedDrawnPolygonData.newColor }); // Set your desired color
        }
    }, [editedDrawnPolygonData]);

    useEffect(() => {
        if (deletedDrawnPolygonId) {
            //@ts-ignore
            const layerToRemove = map._layers[deletedDrawnPolygonId];
            //@ts-ignore
            layerToRemove && map.removeLayer(layerToRemove);
        }
    }, [deletedDrawnPolygonId]);
    /*--------------------------------------------------------------------------*/

    /*--------------------control for measuring tool--------------------*/
    useEffect(() => {
        measuringToolController(measuringToolPolyline, isUsingMeasuringTool, measuringToolDrawn);
    }, [isUsingMeasuringTool, measuringToolDrawn, measuringToolCheckToggle]);

    /*delete measure tool lines when measure tool delete button is clicked*/
    useEffect(() => {
        if (isMeasuringToolDeleteClicked) {
            measuringToolLines.forEach((measureLine: any) => {
                measureLine.removeFrom(featureGroupRef.current);
            });
            setMeasuringToolLines([]);
        }
    }, [isMeasuringToolDeleteClicked]);

    useEffect(() => {
        if (measuringToolLines) {
            dispatch(setIsMeasuringToolDeleteClicked(false));
        }
    }, [measuringToolLines]);

    /*--------------------------------------------------------------------*/

    /*----toggle delete button based on polygon edit id----*/
    useEffect(() => {
        if (multiPolygonEditedId) {
            removeDeleteButton();
        } else {
            restoreDeleteButton();
        }
    }, [multiPolygonEditedId]);
    /*-----------------------------------------------------*/

    /*---generates a new key for edit control which fixes polygon editing/dragging issues---*/
    useEffect(() => {
        setEditControlKey(editControlKey + 1 * Math.random());
    }, [
        singlePolygonCoords,
        isMapZooming,
        isPolygonDragActive,
        isUsingMeasuringTool,
        measuringToolDrawn,
        measuringToolCheckToggle,
        distanceBetweenGeneratedPoints,
        deletedDrawnPolygonId,
        isEarthEngineServiceActive,
        // isEditDrawnPolygons,
    ]);

    /*-------------------------------------------------------------------------------------*/

    return (
        <>
            <FeatureGroup ref={featureGroupRef}>
                <EditControl
                    key={editControlKey}
                    position="topright"
                    onMounted={onMounted}
                    onCreated={onCreated}
                    onEdited={onEdited}
                    onEditStart={onEditStart}
                    onEditStop={onEditStop}
                    onEditMove={onEditMove}
                    onDrawStart={onDrawStart}
                    onDrawStop={onDrawStop}
                    onDeleteStart={onDeleteStart}
                    onDeleteStop={onDeleteStop}
                    onDeleted={onDeleted}
                    draw={drawOptions(isDrawPolygonActive, distanceBetweenGeneratedPoints, mapOptions)}
                    edit={editOptions(isAuthorizedToEdit)}
                />
                {isEditControlMounted && (
                    <>
                        {isEditable && !isMapZooming && <SinglePolygon measuringToolDrawn={measuringToolDrawn} />}
                        {multiPolygonEditedId !== 0 && (
                            <SingleMultiPolygonEditable measuringToolDrawn={measuringToolDrawn} />
                        )}
                    </>
                )}
            </FeatureGroup>
            {!isMapLocked && <EditControlCustomButtons />}
        </>
    );
};

export default EditLayer;
